import {
  CreateDashboardFavoriteApiArg,
  CreateDashboardFavoriteApiResponse,
  DashboardFavorite,
  DashboardType,
  DeleteDashboardFavoriteApiArg,
  DeleteDashboardFavoriteApiResponse,
  ReadChannelDashboardMetricsApiResponse,
  ReadDashboardFavoriteApiArg,
  ReadDashboardFavoriteApiResponse,
  ReadDashboardFavoritesApiArg,
  ReadDashboardFavoritesApiResponse,
  ReadOrganizationDashboardMetricsApiResponse,
  ReadQueueDashboardMetricsApiArg,
  ReadQueueDashboardMetricsApiResponse,
  UpdateDashboardFavoriteApiArg,
  UpdateDashboardFavoriteApiResponse,
} from 'lib-dashboard-types';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';
import {
  WebSocketRealTimeChannelDashboardMetricsChangedInboundMessage,
  WebSocketRealTimeOrganizationDashboardMetricsChangedInboundMessage,
} from 'middleware/webSocket';
import { pubSub } from 'utils/pubSub';

export const dashboardApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readQueueDashboardMetrics: builder.query<
      ReadQueueDashboardMetricsApiResponse,
      ReadQueueDashboardMetricsApiArg
    >({
      query: ({ queueIds }) => ({
        version: 1,
        path: 'analytics/realtime',
        params: {
          type: 'queue' satisfies DashboardType,
          id: queueIds,
        },
      }),
      // Removes the cached data immediately after the last component unsubscribes. This ensures
      // that we always fetch the latest metrics when the user navigates to the dashboard.
      keepUnusedDataFor: 0,
    }),
    readChannelDashboardMetrics: builder.query<
      ReadChannelDashboardMetricsApiResponse,
      void
    >({
      query: () => ({
        version: 1,
        path: 'analytics/realtime',
        params: {
          type: 'channel' satisfies DashboardType,
        },
      }),
      // Removes the cached data immediately after the last component unsubscribes. This ensures
      // that we always fetch the latest metrics when the user navigates to the dashboard.
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe: ReturnType<typeof pubSub.subscribe> | undefined;

        try {
          // Wait for the initial query that loads metrics from the REST API to resolve before
          // proceeding to listening to pubSub events.
          await cacheDataLoaded;

          /**
           * Handles updating the cached data with the new metrics.
           *
           * The metrics are merged because the payload from the WebSocket
           * message may only contain partial metric updates.
           */
          const handleMetricsChanged = (
            payload: WebSocketRealTimeChannelDashboardMetricsChangedInboundMessage,
          ) => {
            updateCachedData((draft) => {
              draft.data.channel_metrics.forEach((currentData) => {
                const mutableData = currentData as DeepWritable<
                  typeof currentData
                >;

                const updatedData = payload.params.channel_metrics.find(
                  (updatedChannel) => updatedChannel.id === mutableData.id,
                );

                Object.assign(mutableData.metrics, updatedData?.metrics);

                // Update any existing SLA policies if they exist in the payload.
                if (updatedData?.sla_policies?.length) {
                  mutableData.sla_policies = mutableData.sla_policies ?? [];

                  mutableData.sla_policies.forEach((currentPolicy) => {
                    const updatedPolicy = updatedData.sla_policies.find(
                      (policy) => policy.id === currentPolicy.id,
                    );

                    if (updatedPolicy) {
                      currentPolicy.id = updatedPolicy.id;

                      currentPolicy.metrics =
                        updatedPolicy.metrics as DeepWritable<
                          typeof updatedPolicy.metrics
                        >;
                    }
                  });
                }
              });
            });
          };

          unsubscribe = pubSub.subscribe(
            'real_time_dashboard:channel_metrics_changed',
            handleMetricsChanged,
          );
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`, in which case
          // `cacheDataLoaded` will throw.
        }

        // `cacheEntryRemoved` will resolve when the cache subscription is no longer active.
        await cacheEntryRemoved;
        // Clean-up the subscription once the `cacheEntryRemoved` promise resolves.
        unsubscribe?.();
      },
    }),
    readOrganizationDashboardMetrics: builder.query<
      ReadOrganizationDashboardMetricsApiResponse,
      void
    >({
      query: () => ({
        version: 1,
        path: 'analytics/realtime',
        params: {
          type: 'organization' satisfies DashboardType,
        },
      }),
      // Removes the cached data immediately after the last component unsubscribes. This ensures
      // that we always fetch the latest metrics when the user navigates to the dashboard.
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe: ReturnType<typeof pubSub.subscribe> | undefined;

        try {
          // Wait for the initial query that loads metrics from the REST API to resolve before
          // proceeding to listening to pubSub events.
          await cacheDataLoaded;

          /**
           * Handles updating the cached data with the new metrics.
           *
           * The metrics are merged because the payload from the WebSocket
           * message only contains partial updates to each metric.
           */
          const handleMetricsChanged = (
            payload: WebSocketRealTimeOrganizationDashboardMetricsChangedInboundMessage,
          ) => {
            updateCachedData((draft) => {
              const mutableData = draft.data
                .organization_metrics[0] as DeepWritable<
                (typeof draft.data.organization_metrics)[0]
              >;

              // The API is typed in a way that `organization_metrics` is an array, but it's
              // guaranteed to only have one item, so we can safely target only the first item.
              mutableData.metrics = mutableData.metrics.map((currentMetric) =>
                Object.assign(
                  currentMetric,
                  payload.params.organization_metrics[0].metrics.find(
                    (updatedMetric) =>
                      updatedMetric.type === currentMetric.type,
                  ),
                ),
              );
            });
          };

          unsubscribe = pubSub.subscribe(
            'real_time_dashboard:organization_metrics_changed',
            handleMetricsChanged,
          );
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`, in which case
          // `cacheDataLoaded` will throw.
        }

        // `cacheEntryRemoved` will resolve when the cache subscription is no longer active.
        await cacheEntryRemoved;
        // Clean-up the subscription once the `cacheEntryRemoved` promise resolves.
        unsubscribe?.();
      },
    }),
    readDashboardFavorites: builder.query<
      ReadDashboardFavoritesApiResponse,
      ReadDashboardFavoritesApiArg
    >({
      query: ({ dashboard = 'queue' }) => ({
        version: 1,
        path: `analytics/realtime/favorites`,
        params: {
          dashboard,
        },
      }),
    }),
    readDashboardFavorite: builder.query<
      ReadDashboardFavoriteApiResponse,
      ReadDashboardFavoriteApiArg
    >({
      query: ({ id }) => ({
        version: 1,
        path: `analytics/realtime/favorites/${id}`,
      }),
    }),
    createDashboardFavorite: builder.mutation<
      CreateDashboardFavoriteApiResponse,
      CreateDashboardFavoriteApiArg
    >({
      query: ({ favorite }) => ({
        version: 1,
        path: `analytics/realtime/favorites`,
        method: 'POST',
        body: favorite,
      }),
      // Handles pessimistic update of the cached API data.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const {
            data: {
              data: { favorite: createdFavorite },
            },
          } = await queryFulfilled;

          dispatch(
            dashboardApi.util.updateQueryData(
              'readDashboardFavorites',
              { dashboard: createdFavorite.dashboard },
              (draft) => {
                const favorites = draft.data.favorites as DeepWritable<
                  typeof draft.data.favorites
                >;

                favorites.push(
                  createdFavorite as DeepWritable<DashboardFavorite>,
                );

                favorites.sort((a, b) => a.name.localeCompare(b.name));
              },
            ),
          );
        } catch (error) {
          /** Do nothing */
        }
      },
    }),
    updateDashboardFavorite: builder.mutation<
      UpdateDashboardFavoriteApiResponse,
      UpdateDashboardFavoriteApiArg
    >({
      query: ({ id, userId, favorite }) => ({
        version: 1,
        path: `analytics/realtime/favorites/${id}`,
        method: 'PATCH',
        body: {
          ...favorite,
          user_id: userId,
        },
      }),
      // Handles pessimistic update of the cached API data.
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const {
            data: {
              data: { favorite: updatedFavorite },
            },
          } = await queryFulfilled;

          dispatch(
            dashboardApi.util.updateQueryData(
              'readDashboardFavorites',
              { dashboard: updatedFavorite.dashboard },
              (draft) => {
                const favorites = draft.data.favorites as DeepWritable<
                  typeof draft.data.favorites
                >;
                const index = draft.data.favorites.findIndex(
                  (_favorite) => _favorite.id === id,
                );

                if (index !== -1) {
                  favorites[index] =
                    updatedFavorite as DeepWritable<DashboardFavorite>;

                  favorites.sort((a, b) => a.name.localeCompare(b.name));
                }
              },
            ),
          );

          dispatch(
            dashboardApi.util.updateQueryData(
              'readDashboardFavorite',
              { id },
              (draft) => {
                const mutableFavorite = draft.data.favorite as DeepWritable<
                  typeof draft.data.favorite
                >;

                Object.assign(mutableFavorite, updatedFavorite);
              },
            ),
          );
        } catch (error) {
          /** Do nothing */
        }
      },
    }),
    deleteDashboardFavorite: builder.mutation<
      DeleteDashboardFavoriteApiResponse,
      DeleteDashboardFavoriteApiArg
    >({
      query: ({ id }) => ({
        version: 1,
        path: `analytics/realtime/favorites/${id}`,
        method: 'DELETE',
      }),
      // Handles pessimistic update of the cached API data.
      async onQueryStarted({ id, dashboard }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            dashboardApi.util.updateQueryData(
              'readDashboardFavorites',
              { dashboard },
              (draft) => {
                const mutableFavorites = draft.data.favorites as DeepWritable<
                  typeof draft.data.favorites
                >;
                const index = draft.data.favorites.findIndex(
                  (_favorite) => _favorite.id === id,
                );

                if (index !== -1) {
                  mutableFavorites.splice(index, 1);
                }
              },
            ),
          );
        } catch (error) {
          /** Do nothing */
        }
      },
    }),
  }),
});
